import { openConfirm } from '@/react/components/modals/confirm';
import { environmentStore } from '@/react/hooks/current-environment-store';

import { recordActionConfirmed, shouldSkipConfirm } from './shouldSkipConfirm';
import { getChangeConfirmationPolicyFromCache } from './getChangeConfirmationPolicyFromCache';

/**
 * URL patterns for requests that do not mutate state and should not trigger
 * the change-confirmation policy dialog (e.g. dry-run, RBAC checks).
 */
const CHANGE_CONFIRMATION_EXEMPT_URL_PATTERNS: RegExp[] = [
  // Dry-run — server evaluates but commits nothing
  // POST /api/endpoints/{id}/kubernetes/helm?dryRun=true
  /[?&]dryRun=true/i,

  // Kubernetes RBAC check — POST body, but is semantically a read
  // POST /api/endpoints/{id}/kubernetes/apis/authorization.k8s.io/v1/selfsubjectaccessreviews
  /selfsubjectaccessreviews/,

  // Helm Git dry-run — evaluates but does not install
  // POST /api/endpoints/{id}/kubernetes/helm/git/dryrun
  /kubernetes\/helm\/git\/dryrun/i,

  // Docker TTY resize — session management, not a deployment change
  // POST /docker/{id}/exec/{execId}/resize
  // POST /docker/{id}/containers/{containerId}/resize
  /docker\/\d+\/(?:exec|containers)\/.+\/resize/i,

  // Edge environment snapshot — triggers tunnel connection, not a deployment change
  // POST /api/endpoints/{id}/snapshot
  /endpoints\/\d+\/snapshot/i,
];

export type ChangeConfirmationRequestConfig = {
  method?: string;
  url?: string;
  params?: unknown;
  /**
   * Unique ID for the originating user action (e.g. a form submission).
   * Once the user confirms a dialog for this ID, all subsequent requests
   * carrying the same ID skip the dialog. Must be unique per submission
   * (e.g. uuidv4()) to avoid stale skips across separate actions.
   */
  actionId?: string;
};

const MUTATING_METHODS = ['post', 'put', 'delete', 'patch'];

// e.g. endpoints/3/..., kubernetes/25801/..., or docker/5/... (baseURL-relative)
const ENVIRONMENT_PATH = /\/?(?:endpoints|kubernetes|docker)\/\d+\//;

// Paths that are endpoint-scoped via endpointId (query or params) and should show the change confirmation policy dialog.
// These paths do not embed the environment ID in the URL segment, so the endpointId is passed as a query param or body param instead.
const ENDPOINT_SCOPED_PATHS = [/stacks/];

/**
 * Extracts the endpointId from the URL query string or params object.
 * Returns the numeric ID if present and matches the current environment, otherwise null.
 */
function extractEndpointId(url: string, params?: unknown): number | null {
  const fromUrl = url.match(/[?&]endpointId=(\d+)/i);
  if (fromUrl) {
    return parseInt(fromUrl[1], 10);
  }
  if (params && typeof params === 'object') {
    const p = params as Record<string, unknown>;
    const raw = p.endpointId;
    if (raw !== undefined && raw !== null) {
      const id = Number(raw);
      return Number.isFinite(id) ? id : null;
    }
  }
  return null;
}

function isEnvironmentScopedRequest(url: string, params?: unknown): boolean {
  if (ENVIRONMENT_PATH.test(url)) {
    return true;
  }

  if (!ENDPOINT_SCOPED_PATHS.some((re) => re.test(url))) {
    return false;
  }

  const requestEndpointId = extractEndpointId(url, params);
  if (requestEndpointId === null) {
    return false;
  }

  // Only trigger the dialog when the request targets the environment that owns the policy
  const { environmentId } = environmentStore.getState();
  return requestEndpointId === environmentId;
}

/**
 * Returns true if the request may proceed (user confirmed or confirmation not required).
 * When config is provided (e.g. from axios), applies method/path/exemption rules first.
 * When config is omitted (e.g. button actions), only runs policy and dialog checks.
 */
export async function requestChangeConfirmation(
  config?: ChangeConfirmationRequestConfig
): Promise<boolean> {
  if (config) {
    if (!MUTATING_METHODS.includes(config.method?.toLowerCase() ?? '')) {
      return true;
    }
    if (isRequestExemptFromChangeConfirmation(config)) {
      return true;
    }
    const isEnvironmentUrl =
      config.url && isEnvironmentScopedRequest(config.url, config.params);
    if (!isEnvironmentUrl) {
      return true;
    }
  }

  // dialog skip check (check for a dialog already open, recently closed, or action already confirmed)
  if (shouldSkipConfirm(config?.actionId)) {
    return true;
  }

  // check the change confirmation policy
  const confirmPolicy = getChangeConfirmationPolicyFromCache();
  if (!confirmPolicy?.ConfirmEnabled?.value) {
    return true;
  }

  // otherwise confirm the request with a dialog. deriveDialogOptions will populate title and message from the policy.
  const confirmed = await openConfirm({});
  if (confirmed && config?.actionId) {
    recordActionConfirmed(config.actionId);
  }
  return confirmed;
}

/**
 * Returns true if the request should skip the change-confirmation policy dialog
 * (e.g. dry-run, selfsubjectaccessreviews). Used by the axios interceptor and
 * callers that need to know if a given request is exempt.
 */
export function isRequestExemptFromChangeConfirmation({
  url = '',
  params,
}: ChangeConfirmationRequestConfig): boolean {
  if (CHANGE_CONFIRMATION_EXEMPT_URL_PATTERNS.some((re) => re.test(url))) {
    return true;
  }

  // dryRun may still be an unserialized object param (Axios) rather than in the URL string
  if (params && typeof params === 'object') {
    const p = params as Record<string, unknown>;
    if (p.dryRun === true || p.dryRun === 'true') {
      return true;
    }
  }

  return false;
}
